import React from 'react';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import Styled from 'styled';
import { useQuery } from '@apollo/client/react/hooks';
import { toGlobalId } from 'graphql-relay';
import classnames from 'classnames';

import SVGIcon from 'lib/images/svg-pure-icon/svg-pure-icon';
import Image from 'lib/images/image-view';

import IconUser from 'four-nets/icons/svg/icon_user-2.svg';
import IconLike from 'four-nets/icons/svg/icon_like-3.svg';
import SelectionItems from 'four-nets/homepage/homepage/selection-items/selection-items';

import query, { RelatedAlbumsQuery, RelatedAlbumsQueryVariables, RelatedAlbumFragment } from './related-albums.graphql';
import defaultStyles from './related-albums.scss';

interface Props {
    styles: typeof defaultStyles;
    category: { id: number };
}

export interface RelatedAlbumProps {
    album: RelatedAlbumFragment;
}

export const RelatedAlbum: React.FC<React.PropsWithChildren<RelatedAlbumProps>> = Styled(defaultStyles)(props => {
    const { album, styles } = props;
    const image = album?.images?.edges[0]?.node!;
    const link = `/blog/${album.user?.username}/album/${album?.slug}`;
    return (
        <div className={styles.album}>
            <Link to={link}>
                <div className={styles.imageWrapper}>
                    <Image
                        sizes={{ xs: 205 }}
                        aspectFill={true}
                        src={image.photoFile?.src!}
                        srcSet={image.photoFile?.srcset!}
                        alt={album.title}
                        linkify={false}
                    />
                </div>
            </Link>
            <h4 className={styles.albumTitle}>
                <Link to={link}>{album.title}</Link>
            </h4>
            <div className={styles.meta}>
                <div className={styles.metaItem}>
                    <SVGIcon height={15} width={13} src={IconUser} />
                    {album.user?.username}
                </div>
                {album.likesCount ? (
                    <div className={styles.metaItem}>
                        <SVGIcon height={15} width={15} src={IconLike} />
                        {album.likesCount}
                    </div>
                ) : null}
            </div>
        </div>
    );
});

const RelatedAlbums: React.FC<Props> = props => {
    const { styles, category } = props;
    const { data } = useQuery<RelatedAlbumsQuery, RelatedAlbumsQueryVariables>(query, {
        variables: { threadCategoryId: toGlobalId('ThreadCategoryNode', category.id.toString()) },
    });
    const topic = data?.threadCategory?.relatedSelectionItems?.edges[0]?.node?.translationKey;

    const selectionItemsHeading = (
        <FormattedMessage id='MORE_ALBUM_SELECTION_ITEMS'>
            {msg => <h3 className={classnames(styles.heading, styles.tagsHeading)}>{msg}</h3>}
        </FormattedMessage>
    );
    return data && data.threadAlbums?.edges.length ? (
        <div>
            <FormattedMessage id='RECOMMENDED_TOPIC_ALBUMS'>
                {msg => (
                    <h3 className={styles.heading}>
                        {msg} <Link to={`/blogs/?selection=${topic}`}>{topic}</Link>
                    </h3>
                )}
            </FormattedMessage>
            <div className={styles.albums}>
                {data?.threadAlbums?.edges.map(edge => (
                    <RelatedAlbum key={edge?.node?.id} album={edge?.node!} />
                ))}
            </div>
            <div className={styles.tagsWrapper}>
                <SelectionItems preceedingItems={[selectionItemsHeading]} />
            </div>
        </div>
    ) : null;
};

export default Styled(defaultStyles)(RelatedAlbums);
